<template>
  <div v-if="eventData && eventData.info1 && eventData.info1.length > 0" class="mb-16">
    <GlobalNavigation />

    <EventHero :event="eventData" class="mb-6 md:mb-20" />

    <BaseContainer>
      <div
        class="flex flex-col justify-between items-start lg:flex-row-reverse gap-4 xl:gap-30"
      >
        <EventTicketArea :event="eventData" :previewMode="previewMode" v-if="!eventData.hide_ticketsale_ticket_info"/>
        <EventParallelTicketArea :event="eventData" v-if="!!eventData.parallel_external_ticketsale_url"/>

        <div class="grow">
          <div class="flex flex-col gap-4 mb-8">
            <BaseInfoBox :title="$t('event.time')">
              <template #icon>
                <CalendarIcon class="w-6 h-6 shrink-0 fill-primary" />
              </template>
              <template #description>
                <EventDate :event="eventData" :showTime="true"/>
              </template>
            </BaseInfoBox>

            <EventMap
              v-if="venue && !eventData.hide_ticketsale_venue_info && !generalStore.customer().hide_venue_on_event_details"
              :venue="venue"
            />
          </div>

          <h2 class="event-title h2">{{ getLocalizedEventValue(eventData, locale, 'info1') }}</h2>
          <strong class="mt-4 mb-6 block whitespace-pre-line">
            {{ getLocalizedEventValue(eventData, locale, 'info2') }}
          </strong>

          <BaseFoldOut>
            <div class="prose" v-html="getLocalizedEventValue(eventData, locale, 'description')"></div>
          </BaseFoldOut>

          <div class="border-t border-surface-grey py-4 mt-10 prose" v-if="doNotShow">
            <h4 class="h4 !mb-6">{{ $t('event.photo_video_social_media') }}</h4>
            <strong>{{ $t('event.social_media') }}</strong>
            <p>{{ $t('event.social_media_notice') }}</p>

            <strong>{{ $t('event.photo_video') }}</strong>
            <p>{{ $t('event.photo_video') }}</p>
          </div>

          <div class="border-t border-surface-grey pt-10" v-if="doNotShow && generalStore.general && generalStore.general.customer">
            <h4 class="h4 mb-2">{{ $t('event.organizer') }}</h4>
            <p class="text-text">
              {{ generalStore.general.customer.name }}
            </p>
          </div>

          <div class="border-t border-surface-grey py-10" v-if="eventCategories && eventCategories.length > 0">
            <h4 class="h4">{{ $t('event.categories') }}</h4>
            <div class="mt-3 flex flex-wrap gap-2">
              <span
                v-for="(eventCategoryName, i) in eventCategories"
                :key="i"
                class="inline-block max-w-[200px] min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm px-2 py-1 font-semibold rounded border-1 border-surface-grey bg-surface-light text-text"
              >{{ eventCategoryName }}</span>
            </div>
          </div>

          <div class="border-t border-surface-grey py-10" v-if="getLocalizedEventValue(eventData, locale, 'remarks') && getLocalizedEventValue(eventData, locale, 'remarks').length > 0">
            <h4 class="h4">{{ remarksHeading }}</h4>
            <p class="mt-3 whitespace-pre-line">
              {{ getLocalizedEventValue(eventData, locale, 'remarks') }}
            </p>
          </div>

          <div class="border-t border-surface-grey py-10" v-if="eventData.event_fields?.orgname?.trim()">
            <h4 class="h4">{{ $t('event.organizer') }}</h4>
            <p class="mt-3 whitespace-pre-line">
              {{ eventData.event_fields?.orgname }}
            </p>
          </div>

        </div>
      </div>
    </BaseContainer>

    <BaseSliderSection
      class="mt-5 md:mt-20"
      :title="$t('event.related_events')"
      v-if="relatedEvents && relatedEvents.length > 0"
    >
      <swiper-slide
        v-for="relatedEvent in relatedEvents"
        :key="relatedEvent.id"
        class="h-auto"
      >
        <EventCard class="h-full" :eventItem="relatedEvent" />
      </swiper-slide>
    </BaseSliderSection>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed,onMounted,ref,watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  import { fetchDynamicEvent } from '@/api/dynamicEvent';
  import { fetchEvent } from '@/api/event';
  import EventCard from '@/components/event/EventCard.vue';
  import EventDate from '@/components/event/EventDate.vue';
  import EventHero from '@/components/event/EventHero.vue';
  import EventMap from '@/components/event/EventMap.vue';
  import EventParallelTicketArea from '@/components/event/EventParallelTicketArea.vue';
  import EventTicketArea from '@/components/event/EventTicketArea.vue';
  import GlobalNavigation from '@/components/globals/GlobalNavigation.vue';
  import CalendarIcon from '@/components/icons/CalendarIcon.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import BaseFoldOut from '@/components/ui/BaseFoldOut.vue';
  import BaseInfoBox from '@/components/ui/BaseInfoBox.vue';
  import BaseSliderSection from '@/components/ui/BaseSliderSection.vue';
  import { useGeneralStore } from '@/stores/general';
  import type { DynamicEventData,EventDetail } from '@/types';
  import { trackViewItem } from '@/utils/analytics';
  import { getLocalizedEventValue } from '@/utils/eventItemUtil';
  import { getLocalizedCategoryValue } from '@/utils/getCategoryLocalization';
  import { useEventStructuredData } from '@/utils/structuredData';

  const route = useRoute();
  const router = useRouter();
  const generalStore = useGeneralStore();
  const doNotShow = ref(false);
  const { locale, t } = useI18n();

  const props = defineProps<{
    shortURL?: string;
    previewMode?: boolean;
  }>();

  // Get true event id from router and/or short url.
  // Closed events use a 96-character eventcode as the :id param — passed as-is to the backend.
  const eventId = computed((): number | string | null => {
    if (props.previewMode && route.query.id) {
      const rawId = route.query.id.toString();
      return rawId.length === 96 ? rawId : parseInt(rawId, 10);
    }

    if (props.shortURL) {
      const shortUrls = generalStore.short_urls();
      if (typeof shortUrls === "object" && !Array.isArray(shortUrls)) {
        const shortUrlMapping = shortUrls[props.shortURL];
        return shortUrlMapping;
      }
    }

    if (!route.params.id) return null;
    const rawId = route.params.id.toString();

    // Only apply when coming from a bare numeric path (/1234) — the legacy /:id(\d+) redirect.
    // Excludes the event-name guard redirect (/event/1234 → /event/1234/slug) which also sets redirectedFrom.
    if (rawId.length !== 96 && route.redirectedFrom && /^\/\d+$/.test(route.redirectedFrom.path)) {
      const shortUrls = generalStore.short_urls();
      if (typeof shortUrls === "object" && !Array.isArray(shortUrls) && rawId in shortUrls) {
        return shortUrls[rawId];
      }
    }

    return rawId.length === 96 ? rawId : parseInt(rawId, 10);
  });

  // Data fetched from the JSON API
  const eventData = ref<EventDetail | null>(null);

  // When fetchEvent returns null while general data is still loading, we defer the
  // not-found redirect: the eventId computed is reactive to short_urls, so it will
  // recompute (and re-trigger this watch) once general data arrives.
  const awaitingGeneralLoad = ref(false);

  // Watch for route parameter changes and reload event data. 
  // When routing from one 'event/x' page to another 'event/y' page, the component is reused and the route param changes, so we need to watch for that and fetch the new event data. If we don't do this, the event data will not update when navigating between events.
  // alternatively make eventData a computed property that depends on eventId.
  watch(
    () => eventId.value,
    (newId) => {      
      if (newId !== null) {
        awaitingGeneralLoad.value = false;
        fetchEvent(newId).then(data => {
          if (!data) {
            if (generalStore.loading) {
              awaitingGeneralLoad.value = true;
              return;
            }
            router.replace({ path: '/not-found' });
            return;
          }
          eventData.value = data;
          trackViewItem(data);
          fetchDynamicEvent(newId).then(dynamicData => {
            if (eventData.value) {
              eventData.value.remaining_tickets = (dynamicData as DynamicEventData).remaining_tickets;
            }
          });
        });
      }
    },
    { immediate: true }
  );

  // Customer-configured remarks_text (per language) replaces the "Remarks" heading label when set
  const remarksHeading = computed(() => {
    const customerOverride = generalStore.general?.customer?.localizations?.[locale.value]?.remarks_text;
    return customerOverride && customerOverride.length > 0 ? customerOverride : t('event.remarks');
  });

  const eventCategories = computed(() => {
    const eventCategoryNames: string[] = [];
    if (generalStore.customer().hide_categories_everywhere) return eventCategoryNames;
    // get category names from general store
    if (eventData.value && generalStore.general && generalStore.general.categories) {
      for (const catId of eventData.value.categories || []) {
        const category = generalStore.general.categories.find(c => c.id == catId);
        if (category && category.localizations?.[locale.value]?.['name']) {
          eventCategoryNames.push(getLocalizedCategoryValue(category, locale.value, 'name'));
        }
      }
    }
    return eventCategoryNames;
  });

  // Find the venue object by event.venue id
  const venue = computed(() => {
    if (!eventData.value || eventData.value.venue == null) return null;
    return generalStore.venues().find(v => v.id === eventData.value!.venue) || null;
  });

  // Get related events from general store filtered by eventData.related_events
  const relatedEvents = computed(() => {
    if (!eventData.value) return [];
    return generalStore.events().filter(
      item =>
        eventData.value!.related_events?.includes(Number(item.id)) &&
        item.id !== eventData.value!.id
    );
  });

  // Fetch general data on mount
  onMounted(() => {
    generalStore.fetchGeneral();
  });

  const headerTitle = computed(() => {
    let title = '';

    if(eventData.value && eventData.value.info1 && generalStore.general && generalStore.general.customer && generalStore.general.customer.alias) {
      title = getLocalizedEventValue(eventData.value, locale.value, 'info1') + ' | ' + generalStore.general.customer.alias;
    }

    return title;
  });

  const headerDescription = computed(() => {
    return eventData.value && eventData.value.info2
      ? getLocalizedEventValue(eventData.value, locale.value, 'info2')
      : '';
  });

  useHead({
    title: headerTitle,
    meta: [
      {
        name: 'description',
        content: headerDescription,
      },
      {
        property: 'og:title',
        content: headerTitle,
      },
      {
        property: 'og:type',
        content: 'event',
      },
      {
        property: 'og:description',
        content: headerDescription,
      },
    ],
  });

  // Structured data (JSON-LD) for search engines
  const customer = computed(() => generalStore.general?.customer);
  useEventStructuredData(eventData, venue, customer, locale);


</script>
